import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  reportService,
  REPORT_REASON_OPTIONS,
  REPORT_CONTEXT_MAX_CHARS,
  ReportReasonCode,
  ReportTargetType,
} from '../services/reportService';

// FEAT-041 — Report dialog reused by profile + message report flows.
// MVP-8.6 (profile) and MVP-8.7 (message). Copy is neutral / elderly-
// friendly per §1.2 and §18.22 ("Tell us what's wrong" not "Report
// violation"). The reportee is never notified.

export type ReportDialogContext =
  | { kind: 'profile'; profileName?: string | null }
  | { kind: 'message'; messageExcerpt?: string | null };

export interface ReportProfileDialogProps {
  open: boolean;
  onClose: () => void;
  targetType: ReportTargetType;
  targetId: string;
  context?: ReportDialogContext;
  onSubmitted?: (reportId: string) => void;
}

const ReportProfileDialog: React.FC<ReportProfileDialogProps> = ({
  open,
  onClose,
  targetType,
  targetId,
  context,
  onSubmitted,
}) => {
  const [reasonCode, setReasonCode] = useState<ReportReasonCode | ''>('');
  const [contextText, setContextText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setReasonCode('');
      setContextText('');
      setError(null);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  const heading =
    targetType === 'PROFILE' ? "Tell us what's wrong" : 'Tell us about this message';

  const subheading =
    targetType === 'PROFILE'
      ? "Your note goes to our review team. The person being reported isn't told."
      : "Your note goes to our review team. The sender isn't told.";

  const handleSubmit = async () => {
    if (!reasonCode) {
      setError('Please choose what feels wrong.');
      return;
    }
    if (contextText.length > REPORT_CONTEXT_MAX_CHARS) {
      setError(`Please keep your note under ${REPORT_CONTEXT_MAX_CHARS} characters.`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await reportService.createReport({
        targetType,
        targetId,
        reasonCode,
        contextText: contextText.trim() || undefined,
      });
      setSuccess(true);
      onSubmitted?.(result.report.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send report.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="report-dialog-title"
      data-testid="report-dialog"
    >
      <DialogTitle id="report-dialog-title">{heading}</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" data-testid="report-dialog-success">
            Thanks for letting us know. We'll take a look quietly.
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {subheading}
            </Typography>
            {context?.kind === 'profile' && context.profileName ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
                data-testid="report-context-profile"
              >
                Reporting profile: <strong>{context.profileName}</strong>
              </Typography>
            ) : null}
            {context?.kind === 'message' && context.messageExcerpt ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontStyle: 'italic' }}
                data-testid="report-context-message"
              >
                &ldquo;{context.messageExcerpt}&rdquo;
              </Typography>
            ) : null}
            <RadioGroup
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value as ReportReasonCode)}
              aria-label="Reason for reporting"
            >
              {REPORT_REASON_OPTIONS.map((option) => (
                <Box key={option.value} sx={{ mb: 1 }}>
                  <FormControlLabel
                    value={option.value}
                    control={<Radio inputProps={{ 'aria-label': option.label }} />}
                    label={option.label}
                  />
                  <FormHelperText sx={{ ml: 4, mt: -0.5 }}>{option.helperText}</FormHelperText>
                </Box>
              ))}
            </RadioGroup>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Anything you'd like us to know? (optional)"
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
              inputProps={{
                maxLength: REPORT_CONTEXT_MAX_CHARS,
                'aria-label': 'Additional context (optional)',
                'data-testid': 'report-dialog-context',
              }}
              helperText={`${contextText.length}/${REPORT_CONTEXT_MAX_CHARS}`}
              sx={{ mt: 2 }}
            />
            {error ? (
              <Alert severity="error" sx={{ mt: 2 }} data-testid="report-dialog-error">
                {error}
              </Alert>
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || !reasonCode}
              data-testid="report-dialog-submit"
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            >
              Send to review team
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportProfileDialog;
